import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApp } from '../context/AppContext';
import { packagesService } from '../services/packagesService';
import { XMarkIcon } from '@heroicons/react/24/outline';

const DateSlotSchema = Yup.object().shape({
  date: Yup.date()
    .min(new Date(), 'Date must be in the future')
    .required('Date is required'),
  time: Yup.string()
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  isAvailable: Yup.boolean(),
});

const DateSlotForm = ({ slotData, isEdit = false }) => {
  const { id, packageId } = useParams();
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  const formik = useFormik({
    initialValues: {
      date: slotData?.date ? slotData.date.split('T')[0] : '',
      time: slotData?.time || '',
      isAvailable: slotData?.isAvailable ?? true,
    },
    validationSchema: DateSlotSchema,
    onSubmit: async (values) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        const slotData = {
          packageId,
          date: values.date,
          time: values.time || undefined,
          isAvailable: values.isAvailable,
        };

        if (isEdit && id) {
          const updatedSlot = await packagesService.updateDateSlot(id, { id, ...slotData });
          dispatch({ type: 'UPDATE_DATE_SLOT', payload: updatedSlot });
        } else {
          const newSlot = await packagesService.createDateSlot(slotData);
          dispatch({ type: 'ADD_DATE_SLOT', payload: newSlot });
        }

        navigate(`/packages/${packageId}`);
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: error.response?.data?.message || 'Operation failed' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
  });

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit ? 'Edit Date Slot' : 'Add Date Slot'}
              </h1>
              <p className="text-gray-600">
                {isEdit ? 'Update date slot information' : 'Add a new date slot to this package'}
              </p>
            </div>
            <button
              onClick={() => navigate(`/packages/${packageId}`)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        <div className="card">
          <form onSubmit={formik.handleSubmit} className="p-6 space-y-6">
            {state.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{state.error}</p>
              </div>
            )}

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Date *
              </label>
              <input
                id="date"
                name="date"
                type="date"
                min={new Date().toISOString().split('T')[0]}
                className={`input-field mt-1 ${
                  formik.touched.date && formik.errors.date
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : ''
                }`}
                value={formik.values.date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.date && formik.errors.date && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.date}</p>
              )}
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                Time (Optional)
              </label>
              <input
                id="time"
                name="time"
                type="time"
                className={`input-field mt-1 ${
                  formik.touched.time && formik.errors.time
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : ''
                }`}
                value={formik.values.time}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave empty for all-day availability
              </p>
            </div>

            <div className="flex items-center">
              <input
                id="isAvailable"
                name="isAvailable"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formik.values.isAvailable}
                onChange={formik.handleChange}
              />
              <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-900">
                Available for booking
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/packages/${packageId}`)}
                className="btn-secondary"
                disabled={state.isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={state.isLoading || !formik.isValid}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {state.isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </div>
                ) : (
                  isEdit ? 'Update Date Slot' : 'Add Date Slot'
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default DateSlotForm;
